import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { ProjectAnalysisSummary } from '../../../entities/analysis';

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

interface HealthRadarChartProps {
    summary?: ProjectAnalysisSummary;
    localStats?: {
        totalIssues: number;
        critical: number;
        high: number;
        medium: number;
        low: number;
        healthScore: number;
        coverage: number;
    };
}

export function HealthRadarChart({ summary, localStats }: HealthRadarChartProps) {
    // Basic calculation for radar axes based on summary data (priority to local)
    const totalIssues = localStats?.totalIssues || (summary?.total_issues_found ?? 0);
    const criticalIssues = localStats?.critical || (summary?.issues_by_severity?.critical ?? 0);

    // Derived scores (0-100)
    const security = totalIssues > 0 ? Math.max(100 - (criticalIssues * 20), 0) : 0;
    const reliability = totalIssues > 0 ? Math.max(100 - (totalIssues * 2), 0) : 0;
    const maintainability = localStats?.healthScore || (summary?.health_score ?? 0);
    const coverage = localStats?.coverage || (summary?.coverage ?? 0);

    const data = {
        labels: ['Security', 'Reliability', 'Maintainability', 'Coverage', 'Documentation', 'Complexity'],
        datasets: [
            {
                label: 'Project Metrics',
                data: [
                    security,
                    reliability,
                    maintainability,
                    coverage,
                    summary?.issues_by_category?.documentation ? Math.max(0, 100 - (summary.issues_by_category.documentation * 5)) : 85,
                    summary?.issues_by_category?.performance ? Math.max(0, 100 - (summary.issues_by_category.performance * 4)) : 75
                ],
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                borderColor: 'rgb(59, 130, 246)',
                pointBackgroundColor: 'rgb(59, 130, 246)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgb(59, 130, 246)'
            }
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            r: {
                angleLines: {
                    color: 'rgba(156, 163, 175, 0.1)'
                },
                grid: {
                    color: 'rgba(156, 163, 175, 0.1)'
                },
                pointLabels: {
                    font: {
                        family: 'monospace',
                        size: 10
                    },
                    color: 'rgb(156, 163, 175)'
                },
                ticks: {
                    display: false,
                    stepSize: 20
                },
                min: 0,
                max: 100
            }
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    return <Radar data={data} options={options} />;
}
